import { Replay } from '@mui/icons-material';
import { IconButton, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import defaultVideoPlayerStyles from '../../styles';
import SignalingConnection from './SignalingConnection';
import { WowzaVideoPlayerProps } from './types';

type Props = Omit<WowzaVideoPlayerProps, 'streamType'>;

const WowzaVideoPlayer: React.FunctionComponent<Props> = (props) => {
  const {
    url,
    applicationName,
    streamName,
    className,
    controls,
    setShowPTZControls,
    retry = true,
    setStreamLoaded,
  } = props;
  const wowza = true;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isStreamAvailable, setIsStreamAvailable] = useState(true);
  const [reload, setReload] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { t } = useTranslation('common');

  useEffect(() => {
    let toggleStreamLoaded: ReturnType<typeof setTimeout>;
    if (!loading) {
      toggleStreamLoaded = setTimeout(() => setStreamLoaded?.(!loading), 700);
    } else {
      setStreamLoaded?.(!loading);
    }
    return () => toggleStreamLoaded && clearTimeout(toggleStreamLoaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const clearVideo = (instance: HTMLVideoElement) => {
    if (instance) {
      instance.pause();
      instance.srcObject = null;
      instance.src = '';
      instance.load();
    }
  };
  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (!isStreamAvailable && !reload) {
      timeout = setTimeout(() => {
        setReload(true);
      }, 1500);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isStreamAvailable, reload]);

  useEffect(() => {
    setShowPTZControls?.(isStreamAvailable);
  }, [isStreamAvailable, setShowPTZControls]);

  useEffect(() => {
    if (!url || !videoRef.current || (!url.startsWith('ws') && !url.startsWith('wss'))) {
      setIsStreamAvailable(false);
      return;
    }
    setLoading(true);
    setIsStreamAvailable(true);
    if (reload) {
      setReload(false);
      return;
    }
    const instance = videoRef.current;

    const signalingConnection = new SignalingConnection({
      url,
      applicationName,
      streamName,
      wowza,
      onTrack: (event: RTCTrackEvent) => {
        setLoading(false);
        setIsStreamAvailable(true);
        if (event.streams[0]) {
          if (instance) {
            instance.srcObject = event.streams[0];
          }
        }
      },
      onDisconnected: () => {
        setLoading(true);
        setIsStreamAvailable(true);
        clearVideo(instance);
      },
      onError: (error) => {
        if (error?.statusDescription) {
          setError(error?.statusDescription);
        }
        setLoading(false);
        setIsStreamAvailable(false);
      },
    });
    return () => {
      signalingConnection?.close();
      clearVideo(instance);
    };
  }, [applicationName, streamName, url, wowza, reload]);

  const classes = defaultVideoPlayerStyles();

  return (
    <div style={{ position: 'relative' }} className={classes.videoWrapper}>
      <video
        className={className ? className : classes.video}
        ref={videoRef}
        playsInline
        autoPlay
        muted
        controls={controls}
      />
      {loading ? (
        <div className={className ? className : classes.loading}>
          <CircularProgress />
        </div>
      ) : !isStreamAvailable ? (
        <div className={className ? className : classes.unavailableWrapper}>
          <div>
            <div className={className ? className : classes.unavailable}>
              <Typography variant='body2'>{error || t('streamUnavailable')}</Typography>
            </div>
            {retry && (
              <div className={className ? className : classes.retryWrapper}>
                <Tooltip title={t('retry')}>
                  <IconButton
                    size='medium'
                    className={className ? className : classes.retryButton}
                    onClick={() => setReload(!reload)}
                  >
                    <Replay fontSize='medium' />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(
  WowzaVideoPlayer,
  (prev, next) =>
    prev.url !== next.url &&
    prev.applicationName !== next.applicationName &&
    prev.streamName !== next.streamName &&
    prev.controls !== next.controls
);
